"use client";

import { RiRainbowLine } from "@remixicon/react";
import { isUpcoming } from "@workspace/shared-utils";
import type { Festival } from "@workspace/types";
import { memo } from "react";
import { Spinner } from "@workspace/ui/components/spinner";
import { useInfiniteScroll } from "@/hooks/shared/use-infinite-scroll";
import type { FestivalFacepileData } from "@/lib/data/shared/facepile-cached";

// Extended facepile data type that includes set information for groups
interface GroupFestivalFacepileData {
	interactions?: Record<
		string,
		Array<{
			id: string;
			username: string | null;
			display_name: string;
			google_avatar_url?: string | null;
			manual_avatar_url?: string | null;
			rating?: number;
			loved?: boolean;
			must_see?: boolean;
			want_to_see?: boolean;
			saw_live?: boolean;
			saw_stream?: boolean;
			content?: string;
		}>
	>;
	sets?: Record<
		string,
		{
			id: string;
			start_time: string | null;
			end_time: string | null;
			alias_name: string | null;
			artist: {
				id: string;
				name: string;
				image_url: string | null;
			};
		}
	>;
}

import { ArtistFestivalCard } from "../cards/artist-festival-card";
import { ArtistEmptyState, ArtistSearchEmptyState } from "../states/";

// Festival type with artist-specific data
interface ArtistFestival
	extends Omit<
		Festival,
		| "schedule_start_hour"
		| "schedule_end_hour"
		| "schedule_overnight"
		| "schedule_day_start_hour"
	> {
	artist_sets_count?: number;
	artist_performance_dates?: string[];
	schedule_status?: "lineup_only" | "complete";
	// Override these fields to allow null (as returned from server)
	schedule_start_hour?: number | null;
	schedule_end_hour?: number | null;
	schedule_overnight?: boolean | null;
	schedule_day_start_hour?: number | null;
}

interface ArtistFestivalsViewProps {
	festivals: ArtistFestival[];
	artistId: string;
	artistName: string;
	fetchNextPage: () => void;
	hasNextPage?: boolean;
	isFetchingNextPage: boolean;
	selectedYear: string;
	searchTerm?: string;
	sortBy?: string;
	onClearSearch?: () => void;
	onClearYear?: () => void;
	isActive?: boolean; // For infinite scroll activation
	facepileData?: FestivalFacepileData | GroupFestivalFacepileData;
}

export const ArtistFestivalsView = memo(function ArtistFestivalsView({
	festivals,
	artistId,
	artistName,
	fetchNextPage,
	hasNextPage,
	isFetchingNextPage,
	selectedYear,
	searchTerm = "",
	sortBy = "",
	onClearSearch,
	onClearYear,
	isActive = true,
	facepileData = {},
}: ArtistFestivalsViewProps) {
	// Enable infinite scroll
	useInfiniteScroll({
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isActive,
	});

	// Note: Filtering is already done server-side via tRPC query
	// No need to filter client-side

	// Separate upcoming and past festivals using shared utility with grace period
	const upcomingFestivals = festivals.filter((f) => {
		return isUpcoming(f.start_date, f.end_date);
	});

	const pastFestivals = festivals.filter((f) => {
		return !isUpcoming(f.start_date, f.end_date);
	});

	if (festivals.length === 0) {
		const hasFilters = searchTerm.trim() !== "" || selectedYear !== "all";

		if (hasFilters && onClearSearch && onClearYear) {
			return (
				<ArtistSearchEmptyState
					searchTerm={searchTerm}
					selectedYear={selectedYear}
					contentType="festivals"
					artistName={artistName}
					onClearSearch={onClearSearch}
					onClearYear={onClearYear}
				/>
			);
		}

		return (
			<ArtistEmptyState
				icon={RiRainbowLine}
				title={
					selectedYear === "all"
						? `${artistName} hasn't performed at any festivals yet`
						: `${artistName} didn't perform at any festivals in ${selectedYear}`
				}
				description="When they perform at festivals, they'll show up here"
			/>
		);
	}

	// Deduplicate festivals to prevent duplicate keys across sections
	const allFestivalIds = new Set();
	const deduplicatedUpcoming = upcomingFestivals.filter((festival) => {
		if (allFestivalIds.has(festival.id)) {
			return false;
		}
		allFestivalIds.add(festival.id);
		return true;
	});
	const deduplicatedPast = pastFestivals.filter((festival) => {
		if (allFestivalIds.has(festival.id)) {
			return false;
		}
		allFestivalIds.add(festival.id);
		return true;
	});

	// Determine section order based on sort direction
	const isOldestFirst = sortBy === "date-asc";
	const sections = isOldestFirst
		? [
				{ data: deduplicatedPast, title: "Past", keyPrefix: "past" },
				{
					data: deduplicatedUpcoming,
					title: "Upcoming",
					keyPrefix: "upcoming",
				},
			]
		: [
				{
					data: deduplicatedUpcoming,
					title: "Upcoming",
					keyPrefix: "upcoming",
				},
				{ data: deduplicatedPast, title: "Past", keyPrefix: "past" },
			];

	return (
		<div className="space-y-6">
			{sections.map(
				(section) =>
					section.data.length > 0 && (
						<div key={section.keyPrefix}>
							<h3 className="mb-4 font-semibold text-foreground text-lg">
								{section.title}
							</h3>
							<div className="space-y-3">
								{section.data.map((festival) => {
									// Extract attendees and set info for groups
									const isGroupData =
										facepileData &&
										"interactions" in facepileData &&
										"sets" in facepileData;
									const attendees = isGroupData
										? (facepileData as GroupFestivalFacepileData)
												.interactions?.[festival.id] || []
										: (facepileData as FestivalFacepileData)?.[festival.id] ||
											[];
									const setInfo = isGroupData
										? (facepileData as GroupFestivalFacepileData).sets?.[
												festival.id
											]
										: undefined;

									return (
										<ArtistFestivalCard
											key={`${section.keyPrefix}-${festival.id}`}
											festival={festival}
											artistId={artistId}
											className="w-full"
											attendees={attendees}
											isFacepileLoading={false}
											setInfo={setInfo}
										/>
									);
								})}
							</div>
						</div>
					),
			)}

			{/* Loading indicator for infinite scroll */}
			{isFetchingNextPage && (
				<div className="flex justify-center py-4">
					<Spinner variant="muted" />
				</div>
			)}
		</div>
	);
});

ArtistFestivalsView.displayName = "ArtistFestivalsView";
